import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChannelBadge,
  type ChannelKind,
  ConfirmAction,
  DetailPage,
  EmptyState,
  Skeleton,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import { useApiClient, useSnackbar } from '@skylabs-digital/react-proto-kit';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notificationsApi } from '../../api/notifications.api';
import { ERROR_CATEGORY_INFO } from '../../constants/error-categories';
import { isRetryable, isStuckInQueue, minutesStuck } from '../../utils/notification-retry';
import { statusTone } from '../../utils/status-tone';
import { JsonViewer } from '../shared/JsonViewer';
import { DeliveryAttemptTimeline } from './DeliveryAttemptTimeline';

interface NotificationDetailProps {
  id: string;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

export function NotificationDetail({ id }: NotificationDetailProps) {
  const navigate = useNavigate();
  const { connector } = useApiClient();
  const { showSnackbar } = useSnackbar();
  const { data: notification, loading, error, refetch } = notificationsApi.useById(id);

  /**
   * `POST /notifications/:id/retry` (RFC 0002 §2 Fase 2) — accepts FAILED
   * and a QUEUED notification stuck without a delivery attempt, 409s
   * otherwise (`INVALID_STATUS_FOR_RETRY`). The connector never throws on a
   * non-2xx (see `data-CWi5Y3kt.es.js:executeRequest`) — it resolves an
   * `ErrorResponse` — so both branches have to be checked explicitly rather
   * than relying on try/catch.
   */
  const handleRetry = async () => {
    const res = await connector.post<{
      notificationId: string;
      status: string;
      retryRound: number;
    }>(`notifications/${id}/retry`, {});
    if (res.success) {
      showSnackbar({ message: 'Retry queued', variant: 'success' });
      refetch();
    } else if (res.kind === 'http' && res.status === 409) {
      showSnackbar({
        message: res.message || 'This notification cannot be retried from its current status.',
        variant: 'error',
      });
    } else {
      showSnackbar({ message: res.message ?? 'Failed to retry notification', variant: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !notification) {
    return (
      <EmptyState
        title="Notification not found"
        description={
          error instanceof Error ? error.message : 'This notification may have been deleted.'
        }
        action={
          <Button variant="outline" onClick={() => navigate('/notifications')}>
            Back to notifications
          </Button>
        }
        className="border-destructive/40 bg-destructive/5"
      />
    );
  }

  const metadata: Record<string, unknown> = {
    ...(notification.metadata ?? {}),
    ...(notification.idempotencyKey ? { idempotencyKey: notification.idempotencyKey } : {}),
    ...(notification.priority ? { priority: notification.priority } : {}),
    ...(notification.scheduledAt ? { scheduledAt: notification.scheduledAt } : {}),
  };

  const canRetry = isRetryable(notification);
  const stuck = isStuckInQueue(notification);

  return (
    <DetailPage
      title={
        <span className="font-mono text-xl">
          {notification.id.substring(0, 12)}
          <span className="text-muted-foreground">…</span>
        </span>
      }
      breadcrumbs={[
        { label: 'Notifications', to: '/notifications' },
        { label: notification.id.substring(0, 8) },
      ]}
      actions={
        canRetry ? (
          <ConfirmAction
            trigger={
              <Button>
                <RotateCw /> Retry
              </Button>
            }
            title="Retry this notification?"
            description={
              stuck
                ? 'This notification never got a delivery attempt — it will be re-queued now.'
                : 'This will create a new delivery attempt for this notification.'
            }
            confirmLabel="Retry"
            onConfirm={handleRetry}
          />
        ) : null
      }
    >
      {stuck ? (
        <div
          role="status"
          data-testid="stuck-banner"
          className="flex items-start gap-3 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <div>
            <p className="font-medium text-foreground">
              Stuck in queue for {minutesStuck(notification.createdAt)} min, no delivery attempts
            </p>
            <p className="text-muted-foreground">
              The enqueue never reached the worker (see the failed outbox events in the DLQ
              Inspector). The sweeper re-queues it automatically; use Retry to do it now.
            </p>
          </div>
        </div>
      ) : null}

      <Card>
        <CardContent className="flex flex-col gap-3 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-sm text-foreground" title={notification.id}>
              {notification.id}
            </span>
            <StatusBadge tone={statusTone(notification.status)}>{notification.status}</StatusBadge>
            <ChannelBadge channel={notification.channel as ChannelKind} />
            <span className="text-xs text-muted-foreground">
              {notification.attemptsCount} attempt{notification.attemptsCount === 1 ? '' : 's'}
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              Created · {new Date(notification.createdAt).toLocaleString()}
            </span>
          </div>
          {notification.lastErrorCategory ||
          notification.lastErrorCode ||
          notification.lastAttemptAt ||
          notification.nextRetryAt ? (
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
              {notification.lastErrorCategory ? (
                <StatusBadge
                  tone={ERROR_CATEGORY_INFO[notification.lastErrorCategory].tone}
                  title={ERROR_CATEGORY_INFO[notification.lastErrorCategory].description}
                >
                  {ERROR_CATEGORY_INFO[notification.lastErrorCategory].label}
                </StatusBadge>
              ) : null}
              {notification.lastErrorCode ? (
                <span className="font-mono text-xs text-muted-foreground">
                  {notification.lastErrorCode}
                </span>
              ) : null}
              {notification.lastAttemptAt ? (
                <span className="text-xs text-muted-foreground">
                  Last attempt · {new Date(notification.lastAttemptAt).toLocaleString()}
                </span>
              ) : null}
              {notification.nextRetryAt ? (
                <span className="text-xs text-muted-foreground">
                  Next retry · {new Date(notification.nextRetryAt).toLocaleString()}
                </span>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recipient</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="To">{notification.to}</Field>
            {notification.userId ? (
              <Field label="User ID">
                <span className="font-mono text-xs">{notification.userId}</span>
              </Field>
            ) : null}
            {notification.tenantId ? (
              <Field label="Tenant ID">
                <span className="font-mono text-xs">{notification.tenantId}</span>
              </Field>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Event type">
              <span className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs">
                {notification.eventType}
              </span>
            </Field>
            {notification.subject ? <Field label="Subject">{notification.subject}</Field> : null}
            {notification.variables && Object.keys(notification.variables).length > 0 ? (
              <Field label="Template variables">
                <div className="space-y-0.5 font-mono text-xs">
                  {Object.entries(notification.variables).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-muted-foreground">{key}:</span> {String(value)}
                    </div>
                  ))}
                </div>
              </Field>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Delivery attempts</CardTitle>
        </CardHeader>
        <CardContent>
          <DeliveryAttemptTimeline attempts={notification.deliveryAttempts ?? []} />
        </CardContent>
      </Card>

      {Object.keys(metadata).length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonViewer data={metadata} />
          </CardContent>
        </Card>
      ) : null}
    </DetailPage>
  );
}
